import React, { useState } from "react";
import { X } from "lucide-react";
import CustomDropdown from "../../Ui_Components/CustomDropdown";
import { createWaterIntake } from "../../../services/subPlans";
import { useAuth } from "../../../context/auth/AuthContext";
import { sendPlanNotificationToUser } from "../../../utils/oneSignalNotification";
import { Button } from "../../Ui_Components/Button";

interface WaterPlanCreatePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void; // ← now just a signal to refresh, no payload needed
  planName: string;
  userId: number;
  onShowToast?: (
    type: "success" | "error" | "warning" | "info",
    title: string,
    message?: string,
    duration?: number,
  ) => void;
}

interface WaterPlanData {
  quantity: string;
  interval: string;
}

const WaterPlanCreatePopup: React.FC<WaterPlanCreatePopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  planName,
  userId,
  onShowToast,
}) => {
  const { user } = useAuth();           
  const nutristId = user?.nutrist_id; 
  const [isCreating, setIsCreating] = useState(false);
  const [waterPlanData, setWaterPlanData] = useState<WaterPlanData>({
    quantity: "",
    interval: "",
  });

  const quantityOptions = [
    { value: "0.5 L", label: "0.5 L" },
    { value: "1 L", label: "1 L" },
    { value: "1.5 L", label: "1.5 L" },
    { value: "2 L", label: "2 L" },
    { value: "2.5 L", label: "2.5 L" },
    { value: "3 L", label: "3 L" },
    { value: "3.5 L", label: "3.5 L" },
    { value: "4 L", label: "4 L" },
    { value: "4.5 L", label: "4.5 L" },
    { value: "5 L", label: "5 L" },
  ];

  const intervalOptions = [
    { value: "0:15 hr", label: "0:15 hr" },
    { value: "0:30 hr", label: "0:30 hr" },
    { value: "0:45 hr", label: "0:45 hr" },
    { value: "1:00 hr", label: "1:00 hr" },
    { value: "1:15 hr", label: "1:15 hr" },
    { value: "1:30 hr", label: "1:30 hr" },
    { value: "1:45 hr", label: "1:45 hr" },
    { value: "2:00 hr", label: "2:00 hr" },
    { value: "2:15 hr", label: "2:15 hr" },
    { value: "2:30 hr", label: "2:30 hr" },
    { value: "2:45 hr", label: "2:45 hr" },
    { value: "3:00 hr", label: "3:00 hr" },
  ];

const isFormComplete = () => Boolean(waterPlanData.quantity);

  const handleSendToClient = async () => {
    if (!isFormComplete()) {
      onShowToast?.(
        "warning",
        "Incomplete form",
        "Please fill in both quantity and interval",
      );
      return;
    }

    setIsCreating(true);

    try {
      const data = await createWaterIntake({
        user_id: userId,
        nutrist_id: nutristId,
        value_unit: waterPlanData.quantity,
        plan_name: planName,
      });

      if (data.success !== 1) {
        throw new Error(data.message || "Failed to create water plan");
      }

      sendPlanNotificationToUser(userId, "water_plan");

      // Close & reset first, then notify parent to refresh
      handleClose();
      onSubmit(); // triggers fetchWaterPlans in WaterPlanTab
      onShowToast?.(
        "success",
        "Water plan created",
        `Plan ${planName} has been sent to the client successfully`,
      );
    } catch (err: any) {
      console.error("Create water plan error:", err);
      onShowToast?.(
        "error",
        "Creation failed",
        err.message || "Something went wrong. Please try again.",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setWaterPlanData({ quantity: "", interval: "" });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="pt-5 py-3 mx-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F]">
            Create water plan
            <span className="ml-2 px-2 py-1 rounded-md text-base 3xl:text-lg bg-[#E8F7FD] text-[#2189D3]">
              {planName}
            </span>
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Quantity */}
            <div>
              <label className="block text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] mb-2">
                Quantity
              </label>
              <CustomDropdown
                options={quantityOptions}
                value={waterPlanData.quantity}
                onChange={(value) =>
                  setWaterPlanData({ ...waterPlanData, quantity: value })
                }
                placeholder="Select quantity"
                searchable={false}
              />
            </div>

            {/* Interval */}
            {/* <div>
              <label className="block text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] mb-2">
                Interval
              </label>
              <CustomDropdown
                options={intervalOptions}
                value={waterPlanData.interval}
                onChange={(value) =>
                  setWaterPlanData({ ...waterPlanData, interval: value })
                }
                placeholder="Select interval"
                searchable={false}
              />
            </div> */}
          </div>
        </div>

        {/* Footer */}
        <div className="py-4 mx-6 border-t border-gray-200 flex items-center justify-end gap-4 flex-shrink-0">
          <Button
            variant="secondary"
            size="md"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSendToClient}
            disabled={isCreating || !isFormComplete()}
          >
            {isCreating && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {isCreating ? "Sending..." : "Send to client"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WaterPlanCreatePopup;
